import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const Container = styled.div`
  padding: 6rem 1.5rem 5rem;
  background: rgb(var(--bg));
  min-height: 100vh;
  max-width: 1180px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 5rem 1.25rem 4rem;
  }

  @media (max-width: 480px) {
    padding: 4.5rem 1rem 3rem;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
  box-sizing: border-box;
`;

const Header = styled.header`
  text-align: center;
  margin-bottom: 40px;
  padding: 2.25rem 1.5rem 1.9rem;
  border-radius: 22px;
  background: rgb(var(--fg) / 0.035);
  color: rgb(var(--fg));
  border: 1px solid rgb(var(--fg) / 0.06);
  backdrop-filter: blur(24px) saturate(1.15);
  -webkit-backdrop-filter: blur(24px) saturate(1.15);
  box-shadow: 0 24px 56px -16px rgb(0 0 0 / calc(0.5 * var(--shadow-mul)));
  position: relative;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background:
      radial-gradient(circle at 20% 20%, rgba(125, 211, 252, 0.15), transparent 45%),
      radial-gradient(circle at 82% 22%, rgba(52, 211, 153, 0.08), transparent 35%);
    pointer-events: none;
  }

  & > * {
    position: relative;
    z-index: 1;
  }

  @media (max-width: 600px) {
    padding: 1.9rem 1rem 1.5rem;
    margin-bottom: 32px;
    border-radius: 18px;
  }
`;

const Title = styled.h1`
  font-size: 2.7rem;
  color: rgb(var(--fg) / 0.95);
  margin: 0;
  line-height: 1.1;
  font-weight: 800;
  letter-spacing: -0.03em;

  @media (max-width: 600px) {
    font-size: 2.1rem;
  }
`;

const Subtitle = styled.h2`
  font-size: 1.15rem;
  font-weight: 500;
  color: rgb(var(--fg) / 0.78);
  margin: 10px 0 12px;
  font-style: italic;
  line-height: 1.6;

  @media (max-width: 600px) {
    font-size: 1rem;
  }
`;

const DatePublished = styled.p`
  font-size: 0.9rem;
  color: rgb(var(--gray) / 0.7);
  margin: 0 0 20px;
  letter-spacing: 0.02em;
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  margin-bottom: 0;
  margin-top: 4px;
`;

const Tag = styled.span`
  background: rgb(var(--fg) / 0.05);
  color: rgb(var(--fg) / 0.9);
  font-size: 0.82rem;
  padding: 7px 14px;
  border-radius: 999px;
  font-weight: 600;
  user-select: none;
  border: 1px solid rgba(125, 211, 252, 0.3);
  backdrop-filter: blur(4px);
  transition: transform 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    background: rgb(var(--fg) / 0.08);
    box-shadow: 0 10px 20px rgb(0 0 0 / calc(0.3 * var(--shadow-mul)));
  }
`;

const CoverImage = styled.img`
  display: block;
  width: 100%;
  max-width: 860px;
  height: 360px;
  object-fit: cover;
  object-position: center;
  margin: 1.5rem auto 1rem;
  border-radius: 18px;
  border: 1px solid rgb(var(--fg) / 0.08);
  box-shadow: 0 18px 36px rgb(0 0 0 / calc(0.45 * var(--shadow-mul)));
  background: rgb(var(--fg) / 0.04);
  filter: brightness(0.92) saturate(0.95);
  transition: filter 0.35s ease;

  &:hover {
    filter: brightness(1) saturate(1);
  }

  @media (max-width: 768px) {
    height: 260px;
  }

  @media (max-width: 480px) {
    height: 200px;
    max-width: 100%;
  }
`;

const Section = styled.section`
  margin-bottom: 1.75rem;
  padding: 1.6rem 1.45rem;
  background: rgb(var(--fg) / 0.035);
  border: 1px solid rgb(var(--fg) / 0.06);
  border-radius: 20px;
  backdrop-filter: blur(24px) saturate(1.15);
  -webkit-backdrop-filter: blur(24px) saturate(1.15);
  box-shadow: 0 24px 56px -16px rgb(0 0 0 / calc(0.5 * var(--shadow-mul)));

  @media (max-width: 600px) {
    margin-bottom: 1.25rem;
    padding: 1.2rem 1rem;
    border-radius: 16px;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.45rem;
  color: #7dd3fc;
  margin-bottom: 16px;
  border-bottom: 1px solid rgba(125, 211, 252, 0.25);
  padding-bottom: 10px;
  font-weight: 800;
  letter-spacing: -0.02em;

  @media (max-width: 600px) {
    font-size: 1.2rem;
    margin-bottom: 12px;
    padding-bottom: 8px;
  }
`;

const Paragraph = styled.p`
  font-size: 1rem;
  line-height: 1.85;
  color: rgb(var(--fg) / 0.82);
  margin: 0 0 1rem 0;

  @media (max-width: 600px) {
    font-size: 0.95rem;
    line-height: 1.75;
  }
`;

const TwoColumn = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.25rem;
  margin-top: 0.5rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const InnerCard = styled.div`
  background: rgb(var(--fg) / 0.045);
  border: 1px solid rgba(125, 211, 252, 0.2);
  border-radius: 14px;
  padding: 1.1rem 1.2rem;
`;

const InnerCardTitle = styled.h4`
  font-size: 0.95rem;
  font-weight: 700;
  color: #7dd3fc;
  margin: 0 0 0.55rem 0;
  letter-spacing: -0.01em;
`;

const CodeBlock = styled.pre`
  background: rgb(0 0 0 / calc(0.45 * var(--shadow-mul)));
  color: rgb(var(--fg) / 0.86);
  padding: 1.15rem;
  border-radius: 16px;
  font-family: 'Source Code Pro', monospace, monospace;
  font-size: 0.88rem;
  line-height: 1.65;
  overflow-x: auto;
  margin: 20px 0;
  border: 1px solid rgb(var(--fg) / 0.06);
  box-shadow: 0 16px 36px rgb(0 0 0 / calc(0.35 * var(--shadow-mul)));

  @media (max-width: 600px) {
    font-size: 0.8rem;
    padding: 0.95rem;
    border-radius: 14px;
  }
`;

const Figure = styled.figure`
  margin: 0;
`;

const Image = styled.img`
  display: block;
  max-width: 680px;
  width: 100%;
  margin: 20px auto 10px;
  border-radius: 18px;
  border: 1px solid rgb(var(--fg) / 0.08);
  box-shadow: 0 16px 34px rgb(0 0 0 / calc(0.45 * var(--shadow-mul)));
  background: rgb(var(--fg) / 0.04);
`;

const ImageDark = styled(Image)`
  [data-theme='light'] & {
    display: none;
  }
`;

const ImageLight = styled(Image)`
  display: none;

  [data-theme='light'] & {
    display: block;
  }
`;

const ThemedImage = ({ base, alt }) => (
  <>
    <ImageDark src={`/images/${base}_dark.png`} alt={alt} loading="lazy" />
    <ImageLight src={`/images/${base}_light.png`} alt={alt} loading="lazy" />
  </>
);

const Caption = styled.figcaption`
  font-size: 0.9rem;
  color: rgb(var(--gray) / 0.7);
  text-align: center;
  margin: 12px auto 20px;
  max-width: 680px;
  background: rgb(var(--fg) / 0.04);
  border-radius: 12px;
  padding: 0.6rem 0.75rem;
  border: 1px solid rgb(var(--fg) / 0.06);
`;

const CTABox = styled.div`
  margin: 1.25rem 0 0;
  padding: 1.15rem 1.2rem;
  border-radius: 18px;
  background: rgb(var(--fg) / 0.04);
  border: 1px solid rgba(52, 211, 153, 0.25);
  box-shadow: 0 12px 28px rgb(0 0 0 / calc(0.35 * var(--shadow-mul)));

  @media (max-width: 480px) {
    padding: 1rem;
    border-radius: 16px;
  }
`;

const CTATitle = styled.h3`
  margin: 0 0 0.45rem 0;
  font-size: 1.02rem;
  font-weight: 800;
  color: #34d399;
`;

const CTAText = styled.p`
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.75;
  color: rgb(var(--fg) / 0.82);

  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const CTALink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: 0.9rem;
  padding: 0.78rem 1.25rem;
  border-radius: 999px;
  background: rgb(var(--fg) / 0.06);
  color: rgb(var(--fg) / 0.92);
  border: 1px solid rgb(var(--fg) / 0.14);
  text-decoration: none;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  transition: transform 0.25s, border-color 0.25s, background 0.25s;

  &:hover {
    transform: translateY(-1px);
    background: rgb(var(--fg) / 0.1);
    border-color: rgba(52, 211, 153, 0.5);
  }
`;

const InlineLink = styled.a`
  color: #7dd3fc;
  font-weight: 600;
  text-decoration: underline;
  text-decoration-color: rgba(125, 211, 252, 0.4);
  text-underline-offset: 3px;

  &:hover {
    text-decoration-color: currentColor;
  }

  [data-theme='light'] & {
    color: #0284c7;
    text-decoration-color: rgba(2, 132, 199, 0.35);
  }
`;

const SourceList = styled.ul`
  margin: 0;
  padding-left: 1.4rem;
  color: rgb(var(--fg) / 0.7);
`;

const SourceItem = styled.li`
  font-size: 0.88rem;
  line-height: 1.75;
  margin-bottom: 0.45rem;

  a {
    color: #7dd3fc;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  [data-theme='light'] & a {
    color: #0284c7;
  }
`;

const BackButtonWrapper = styled.div`
  text-align: center;
  margin-top: 28px;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgb(var(--fg) / 0.06);
  color: rgb(var(--fg) / 0.92);
  border: 1px solid rgb(var(--fg) / 0.14);
  border-radius: 999px;
  padding: 0.9rem 2rem;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  cursor: pointer;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  transition: transform 0.25s, border-color 0.25s, background 0.25s;
  user-select: none;

  &:hover {
    transform: translateY(-1px);
    background: rgb(var(--fg) / 0.1);
    border-color: rgba(125, 211, 252, 0.5);
  }

  @media (max-width: 600px) {
    width: 100%;
    padding: 0.85rem 1.5rem;
    font-size: 0.78rem;
  }
`;

export const HospioMetadata = {
  title: 'Hospio — Seasonal Employment Marketplace',
  description: 'Capstone business plan for a hospitality hiring platform, grounded in self-collected data: a scraped census of 10,127 Greek hotels, competitor listing datasets, a 27-respondent employer survey, and a 122-chart analysis pipeline.',
  tags: ['Market Research', 'Data Analysis', 'Entrepreneurship', 'Business Planning'],
  languages: ['Python'],
  datePublished: '2026-04-13',
  coverImage: '/images/hospio_hero.png',
  path: '/projects/hospio',
};

const Hospio = () => {
  const navigate = useNavigate();
  return (
    <Container>
      <ContentWrapper>
        <Header>
          <Title>Hospio — Seasonal Employment Marketplace</Title>
          <Subtitle>A research-driven business plan for fixing seasonal hospitality hiring in Greece</Subtitle>
          <DatePublished>Published on April 13, 2026</DatePublished>
          <CoverImage src={HospioMetadata.coverImage} alt="Hospio landing page hero section mockup" />
          <TagsContainer>
            {HospioMetadata.tags.map(tag => (
              <Tag key={tag}>{tag}</Tag>
            ))}
          </TagsContainer>
        </Header>

        <Section>
          <SectionTitle>Overview</SectionTitle>
          <Paragraph>
            For my capstone in Entrepreneurship Management at Deree (The American College of Greece), I wrote a complete business plan for Hospio: a two-sided hiring and workforce coordination platform for the Greek hospitality sector, entering through 3–5 star hotels in high-tourism regions. The plan covers industry analysis, marketing, operations, organisation, and a 24-month financial model.
          </Paragraph>
          <Paragraph>
            The problem it targets is well documented: between 2021 and 2024, Greek hotels were left with an estimated 50,000 positions unfilled every year (Research Institute for Tourism, 2024), and press reporting put the 2025 gap at around 80,000 across tourism and food service (Salourou, 2025). This is happening inside a hotel sector generating roughly €11.5 billion in gross value added and growing about 8.5% year on year (Hellenic Chamber of Hotels, 2025). Demand is not the problem; matching people to jobs is.
          </Paragraph>
          <Paragraph>
            The plan itself is the packaging. What I actually set out to practice was the research underneath it: treating every claim as something to measure, and collecting the data myself whenever published statistics couldn't answer the question. Every figure below comes from that work, and every number carries its source.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>Building the Evidence Base</SectionTitle>
          <Paragraph>
            The evidence sits in three layers: official statistics, datasets I collected myself, and a primary survey.
          </Paragraph>
          <TwoColumn>
            <InnerCard>
              <InnerCardTitle>Official Statistics</InnerCardTitle>
              <Paragraph style={{ margin: 0, fontSize: '0.95rem' }}>
                ELSTAT national accounts and employment series, INSETE and Research Institute for Tourism industry studies, Hellenic Chamber of Hotels performance tables, DYPA unemployment registers, and Bank of Greece travel receipts. Where findings existed only as tables inside PDF reports, I digitised them by hand so they could be analysed and charted.
              </Paragraph>
            </InnerCard>
            <InnerCard>
              <InnerCardTitle>A Hotel Census, Scraped</InnerCardTitle>
              <Paragraph style={{ margin: 0, fontSize: '0.95rem' }}>
                Official datasets existed, but as aggregates: room counts in buckets, hotel totals by region. To get underneath them, I wrote a Python scraper for the Hellenic Chamber of Hotels' public directory and collected raw records for all 10,127 hotels, with coordinates, room counts, star ratings, and operating schedules, enriching the ELSTAT and Chamber tables with non-aggregated data (Appendix A6). The dataset is public at <InlineLink href="https://greek-hotels-map.vercel.app" target="_blank" rel="noopener noreferrer">greek-hotels-map.vercel.app</InlineLink>, with an interactive map, general and seasonality analysis pages, and a full download.
              </Paragraph>
            </InnerCard>
            <InnerCard>
              <InnerCardTitle>Competitor Listings, Measured</InnerCardTitle>
              <Paragraph style={{ margin: 0, fontSize: '0.95rem' }}>
                Rather than take competitors' press coverage at face value, I scraped their platforms: 282 active listings from Gigpossible (November 2025 – March 2026) and all 406 listings from Sezonly, with salaries, benefits, locations, and application counts (Appendices A4–A5).
              </Paragraph>
            </InnerCard>
            <InnerCard>
              <InnerCardTitle>Primary Survey</InnerCardTitle>
              <Paragraph style={{ margin: 0, fontSize: '0.95rem' }}>
                A Greek-language survey of hospitality employers covering hiring challenges, channel effectiveness, time-to-fill, and willingness to pay (Appendix A7). I sent it out late, about a week before the research had to be finalised, so it closed at 27 responses. The recipient pool was broad enough that I treat the results as a directional read on employer consensus, not a statistically robust sample.
              </Paragraph>
            </InnerCard>
          </TwoColumn>
          <Paragraph style={{ marginTop: '1.25rem' }}>
            Everything downstream is reproducible: a config-driven Python pipeline (pandas, matplotlib, cartopy) turns these sources into the plan's 122 charts, each with its source attribution recorded, so any figure can be traced back to its data and regenerated.
          </Paragraph>
          <CodeBlock>{`Official sources     ELSTAT · INSETE · RIT · Hellenic Chamber of Hotels · DYPA · Bank of Greece
Registry scraper     10,127 hotels: stars, rooms, coordinates, operating period
Competitor scrapers  282 Gigpossible + 406 Sezonly listings
Employer survey      27 hotel operators, Greek-language instrument
Chart pipeline       config-driven → 122 charts, per-chart source attribution`}</CodeBlock>
        </Section>

        <Section>
          <SectionTitle>What the Data Showed</SectionTitle>
          <Paragraph>
            With the caveat above in mind (these figures hint at where the consensus sits rather than prove it), the survey sharpened the problem beyond a generic shortage. Employers ranked finding qualified candidates as the most severe of nine hiring challenges (4.1/5, with 85% rating it 4 or 5), two out of three reported needing two weeks or more to fill a role, and reliability kept coming up: one respondent recalled a hire who "came for a week and then disappeared" (Appendix A7).
          </Paragraph>
          <Figure>
            <ThemedImage base="hospio_hiring_challenges" alt="Severity ranking of nine hiring challenges rated by Greek hospitality employers" />
            <Caption>Severity ranking of nine hiring challenges rated by hospitality employers on a 1–5 scale. Source: primary survey, N=27 hotel operators (Appendix A7).</Caption>
          </Figure>
          <Paragraph>
            The scraped hotel census answered the "where." Mapping all hotels by operating period shows seasonal supply concentrated in the island regions while year-round properties cluster in urban centres and the mainland. That split defines Hospio's entry geography: the Aegean, Ionian, Crete, and select urban hubs.
          </Paragraph>
          <Figure>
            <ThemedImage base="hospio_seasonal_map" alt="Maps of Greece comparing seasonal versus year-round hotel locations" />
            <Caption>Seasonal vs year-round hotels in Greece. Seasonal supply dominates island regions; year-round properties cluster in urban centres. Source: Hellenic Chamber of Hotels online registry (grhotels.gr), scraped dataset, N=10,127 (Appendix A6).</Caption>
          </Figure>
          <Paragraph>
            The same census grounded the market sizing. The total addressable market is all 10,104 Greek hotels (Research Institute for Tourism, 2025). The serviceable addressable market narrows that to the 5,706 hotels rated 3–5 stars, the segment that dominates capacity and accounts for a disproportionate share of the sector's employment and turnover (Research Institute for Tourism, 2025; Hellenic Chamber of Hotels, 2025). The serviceable obtainable market then starts from a subset of the SAM, the 4,021 of those hotels located in high-tourism regions (the Aegean, the Ionian islands, Crete, and select urban hubs), and applies a 2.47% B2B SaaS conversion benchmark (First Page Sage, 2025) to reach a realistic Year 1 acquisition target of roughly 101 hotels. This is, of course, an estimate built for the assignment: the value of the exercise is that each narrowing step is tied to a real count or a published benchmark, giving a deliberately modest view of the theoretical market rather than a prediction.
          </Paragraph>
          <Figure>
            <ThemedImage base="hospio_tam_sam_som" alt="TAM SAM SOM market sizing funnel for Hospio" />
            <Caption>Market sizing funnel: TAM 10,104 hotels → SAM 5,706 (3–5 star) → SOM ≈101, from the 4,021 SAM hotels in high-tourism regions at a 2.47% conversion benchmark. Sources: Research Institute for Tourism (2025); Hellenic Chamber of Hotels (2025); First Page Sage (2025).</Caption>
          </Figure>
          <Paragraph>
            The competitor datasets were the most revealing. Gigpossible, the closest direct competitor, had reported 28,500 users by January 2026 (Tzavella, 2026), yet the scraped listings held 388 applications across 282 jobs, an average of 1.4 per listing, with a single luxury group (Sani/Ikos) behind 49.6% of active listings (Appendix A4). My reading is that this is a positioning problem rather than poor execution: a gig-work platform attracts people who want short-term flexibility, while seasonal employers in Greece need staff who commit to a multi-month season (Omar &amp; Jamil, 2025). Those two pulls point in opposite directions, which makes a general gig marketplace an awkward tool for season-long hospitality hiring. Sezonly raised a different flag: 405 of its 406 listings were created inside a ten-day window, 110 of them on a single day, a pattern that suggests supply seeded before launch rather than organic employer demand (Appendix A5).
          </Paragraph>
          <Figure>
            <ThemedImage base="hospio_gigpossible_concentration" alt="Gigpossible employer concentration analysis showing Sani/Ikos share of listings" />
            <Caption>Employer concentration on Gigpossible: one group accounts for half of all active listings, a concentration its press coverage does not show. Source: author's analysis of scraped Gigpossible listings (Appendix A4).</Caption>
          </Figure>
          <Paragraph>
            A postscript from after submission: in late May 2026, Gigpossible won the startup competition at Panathenea 2026, taking the €25,000 Visa Innovation Program prize along with $250,000 in Microsoft credits, and the coverage now cites 53,000 users (Startupper, 2026). The recognition is deserved, and it confirms that investors see the same gap in this market. Given the application volumes above, that user figure most likely counts total sign-ups rather than active job seekers, which would be a much smaller number. The distinction between the two ventures stands either way: Gigpossible is a broad, gig-based staffing platform, while Hospio is designed specifically for hospitality staff and professionals and the season-long employment cycle.
          </Paragraph>
          <Paragraph>
            Positioning the alternatives on hospitality specialisation against breadth of service showed the gap Hospio targets: job boards are broad but shallow in this sector, agencies are specialised but narrow and expensive, and neither direct competitor combines focus with full-lifecycle scope. The perceptual map is a rough visualisation rather than a formal study; I assigned the rankings myself to make the positioning argument legible, and a professional version would rest on far more detailed criteria.
          </Paragraph>
          <Figure>
            <ThemedImage base="hospio_perceptual_map" alt="Competitive perceptual map positioning Hospio against six alternative solutions" />
            <Caption>Perceptual map of Hospio and six alternatives; bubble size reflects relevant talent pool. A rankings-based sketch of positioning, from the author's competitive assessment (Appendix A2).</Caption>
          </Figure>
        </Section>

        <Section>
          <SectionTitle>The Plan It Packaged Into</SectionTitle>
          <Paragraph>
            Hospio is deliberately not a job board. It supports the full employment lifecycle: candidate discovery, identity and credential verification, structured screening, onboarding, reliability tracking, and rehire and replacement workflows. The intent is to stay in the loop from the job posting to the last shift of the season. Employers pay; workers use it free.
          </Paragraph>
          <Paragraph>
            The marketing plan is built around two-sided marketplace dynamics. Platforms like this create value by enabling direct interactions between two distinct groups of users (Narayanan et al., n.d.), so each side's growth has to feed the other: more active workers make subscriptions worth paying for, and more listings keep workers coming back. Acquisition is therefore modelled per side, on two separate seasonality indices. Employers are expected to sign up in the pre- and post-season planning windows and go quiet mid-summer, while worker sign-ups spike as the season approaches; the estimated monthly budgets for direct sales, trade expos, and paid channels follow those curves, with churn and cost-per-lead assumptions taken from published benchmarks.
          </Paragraph>
          <TwoColumn>
            <InnerCard>
              <InnerCardTitle>Pricing</InnerCardTitle>
              <Paragraph style={{ margin: 0, fontSize: '0.95rem' }}>
                Employer-side subscriptions: Basic €150/mo, Premium €250/mo, Enterprise €500/mo (from Month 9, targeting hotel groups), plus €25 featured listings. Tier splits are proxied from market structure; the Premium share, for example, mirrors the proportion of 4–5 star hotels (Research Institute for Tourism, 2025).
              </Paragraph>
            </InnerCard>
            <InnerCard>
              <InnerCardTitle>Trajectory</InnerCardTitle>
              <Paragraph style={{ margin: 0, fontSize: '0.95rem' }}>
                Estimated revenue grows from €106,395 in Year 1 to €274,337 in Year 2 at a blended ARPU of €195.99 rising to €208.72. Estimated monthly break-even lands in Month 17, with 135 active customers by Month 24, 33.6% above the SOM target.
              </Paragraph>
            </InnerCard>
            <InnerCard>
              <InnerCardTitle>Funding &amp; Cost Base</InnerCardTitle>
              <Paragraph style={{ margin: 0, fontSize: '0.95rem' }}>
                The assignment brief provided the starting resources: €250,000 in equity plus a €150,000 revolving credit line at 9.4%. The exercise was deploying them credibly, not raising them. Estimated CAPEX totals €140,123 over 24 months, and estimated OPEX rises from €216,476 to €276,699 with a 7-person team growing to 8.
              </Paragraph>
            </InnerCard>
            <InnerCard>
              <InnerCardTitle>User Liquidity</InnerCardTitle>
              <Paragraph style={{ margin: 0, fontSize: '0.95rem' }}>
                Marketplaces live or die on liquidity, so the plan tracks a target of 7 active users per listing, set at five times the 1.4 applicants per listing measured on the closest competitor (Appendix A4.3). The modelled user base averages 2.6 times that target, suggesting the customer acquisition assumptions may even be understated.
              </Paragraph>
            </InnerCard>
          </TwoColumn>
          <Figure style={{ marginTop: '1.25rem' }}>
            <ThemedImage base="hospio_break_even" alt="Monthly revenue versus costs chart showing break-even at month 17" />
            <Caption>Estimated monthly revenue vs costs over 24 months, with break-even in Month 17. Cost spikes reflect trade expos (Months 2 and 14) and iOS development. Source: Hospio financial model (Section 6.5 of the plan).</Caption>
          </Figure>
          <Paragraph>
            All of these figures are estimates: theoretical returns from an optimised version of the business, not a forecast. The 12-sheet workbook behind them is built to be treated that way. It is generated end-to-end by a script, and every assumption (seasonality curves, churn, conversion rates, headcount timing) is a parameter, so I could change one and have every downstream table and chart regenerate consistently instead of hand-editing spreadsheet cells.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>What I Learned</SectionTitle>
          <Paragraph>
            The habit that stuck with me most is going to the data directly instead of quoting what's written about it. Reading Gigpossible's press coverage, I had them down as the incumbent to worry about; scraping their listings showed 1.4 applicants per job and half the supply coming from one hotel group. Beyond changing the threat assessment, the concentration figure gave me a usable input, since it roughly answers how many listings a single hotel-group partnership could bring. Nothing in the published coverage would have gotten me there.
          </Paragraph>
          <Paragraph>
            My clearest mistake was timing. I sat on the survey for most of the semester, half-convinced the secondary sources would cover me, and sent it out a week before the research had to be finalised. Twenty-seven responses in a few days suggests people wanted to talk about the problem, which makes the timing sting more: a version sent in month one could have been a genuinely strong dataset instead of a directional one. Primary collection is the slowest pipeline in the project; it should have started first, not last.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>Live Prototype</SectionTitle>
          <Paragraph>
            Alongside the plan I built a working demo of the platform. The landing page in the cover image is a design mockup from that effort.
          </Paragraph>
          <CTABox>
            <CTATitle>Try the Hospio demo</CTATitle>
            <CTAText>
              The prototype lives at hospio.gr. It runs on free-tier infrastructure, so it may need a moment to wake up if it hasn't been visited recently.
            </CTAText>
            <CTALink href="https://www.hospio.gr" target="_blank" rel="noopener noreferrer">
              Visit hospio.gr
            </CTALink>
          </CTABox>
        </Section>

        <Section>
          <SectionTitle>Sources</SectionTitle>
          <SourceList>
            <SourceItem>
              First Page Sage. (2025). <a href="https://firstpagesage.com/seo-blog/b2b-saas-funnel-conversion-benchmarks-fc/" target="_blank" rel="noopener noreferrer">B2B SaaS funnel conversion benchmarks</a>.
            </SourceItem>
            <SourceItem>
              Hellenic Chamber of Hotels. (2025). <a href="https://www.grhotels.gr/pinakes-me-tis-vasikes-ksenodocheiakes-metavlites-gia-tis-epidoseis-ton-ksenodocheion-to-2024/" target="_blank" rel="noopener noreferrer">Tables with the basic hotel variables for hotel performance in 2024</a>.
            </SourceItem>
            <SourceItem>
              Narayanan, C., Tielking, A., &amp; Wadhar, H. (n.d.). <a href="https://articles.sequoiacap.com/two-sided-marketplaces-and-engagement" target="_blank" rel="noopener noreferrer">Two-sided marketplaces and engagement</a>. Sequoia Capital.
            </SourceItem>
            <SourceItem>
              Omar, N., &amp; Jamil, R. (2025). <a href="https://doi.org/10.47772/ijriss.2025.90400354" target="_blank" rel="noopener noreferrer">The shift from traditional jobs to gig work: Rethinking career development in a flexible economy</a>. International Journal of Research and Innovation in Social Science.
            </SourceItem>
            <SourceItem>
              Research Institute for Tourism. (2024). <a href="https://www.itep.gr/review/%CE%B7-%CE%B1%CF%80%CE%B1%CF%83%CF%87%CF%8C%CE%BB%CE%B7%CF%83%CE%B7-%CF%83%CF%84%CE%B1-%CE%BE%CE%B5%CE%BD%CE%BF%CE%B4%CE%BF%CF%87%CE%B5%CE%AF%CE%B1-2024/" target="_blank" rel="noopener noreferrer">Employment in Hotels 2024</a>.
            </SourceItem>
            <SourceItem>
              Research Institute for Tourism. (2025). <a href="https://www.grhotels.gr/app/uploads/2025/02/01_Hotels_2024_Total.pdf" target="_blank" rel="noopener noreferrer">Hotel capacity of Greece 2024</a>.
            </SourceItem>
            <SourceItem>
              Salourou, R. (2025). <a href="https://www.kathimerini.gr/economy/563635642/me-megales-elleipseis-xekina-i-sezon-80-000-kenes-theseis-se-toyrismo-kai-estiasi/" target="_blank" rel="noopener noreferrer">The season begins with major shortages: 80,000 vacant positions in tourism and food service</a>. Kathimerini.
            </SourceItem>
            <SourceItem>
              Startupper. (2026). <a href="https://startupper.gr/news/261656/gig-possible-i-megali-nikitria-tou-startup-competition-sta-panathenea-2026/" target="_blank" rel="noopener noreferrer">GigPossible: The big winner of the Startup Competition at Panathenea 2026</a>.
            </SourceItem>
            <SourceItem>
              Tzavella, A. (2026). <a href="https://www.noupou.gr/stories/south-stories/gigpossible-i-start-up-apo-ta-notia-proastia-pou-psaxnei-kai-vriskei-douleia-gia-tin-gen-z/" target="_blank" rel="noopener noreferrer">GigPossible: The start-up that finds work for Gen Z</a>. NouPou.
            </SourceItem>
            <SourceItem>
              Appendices A2, A4–A7 refer to the capstone's own analyses: the competitive assessment, the Gigpossible and Sezonly listing datasets, the Hellenic Chamber of Hotels directory census, and the employer survey.
            </SourceItem>
          </SourceList>
        </Section>

        <BackButtonWrapper>
          <BackButton onClick={() => navigate('/projects')}>Back to Projects</BackButton>
        </BackButtonWrapper>
      </ContentWrapper>
    </Container>
  );
};

export default Hospio;
